import { create } from 'zustand';
import { api } from '../services/api';

// Mock users database (for demo without backend)
const MOCK_USERS = {
  'admin@helpdeck.local': {
    id: '850e8400-e29b-41d4-a716-446655440001',
    email: 'admin@helpdeck.local',
    password: '123456789',
    full_name: 'Admin User',
    role: 'admin'
  },
  'john@helpdeck.local': {
    id: '850e8400-e29b-41d4-a716-446655440002',
    email: 'john@helpdeck.local',
    password: 'password123',
    full_name: 'John Support',
    role: 'agent'
  },
  'jane@helpdeck.local': {
    id: '850e8400-e29b-41d4-a716-446655440003',
    email: 'jane@helpdeck.local',
    password: 'password123',
    full_name: 'Jane Assistant',
    role: 'agent'
  }
};

export const useAuthStore = create((set) => ({
  user: null,
  token: null,
  loading: false,
  error: null,

  // Initialize auth from localStorage
  initializeAuth: async () => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        set({ user: JSON.parse(userData), token, loading: false });
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_data');
        set({ token: null, user: null, loading: false });
      }
    }
  },

  // Login - with mock authentication
  login: async (email, password, rememberMe = false) => {
    try {
      set({ loading: true, error: null });
      
      // Try real API first
      try {
        const response = await api.post('/auth/login', {
          email,
          password,
          rememberMe,
        });

        const { access_token, user } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('user_data', JSON.stringify(user));
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        set({ user, token: access_token, loading: false });
        return { success: true };
      } catch (apiError) {
        // Fallback to mock authentication
        const mockUser = MOCK_USERS[email];
        
        if (!mockUser) {
          throw new Error('User not found');
        }
        
        if (mockUser.password !== password) {
          throw new Error('Invalid password');
        }

        const mockToken = 'mock_jwt_' + Math.random().toString(36).substring(7);
        localStorage.setItem('access_token', mockToken);
        localStorage.setItem('user_data', JSON.stringify(mockUser));
        
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        set({ user: mockUser, token: mockToken, loading: false });
        return { success: true };
      }
    } catch (error) {
      const message = error.message || 'Login failed';
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('rememberMe');
      set({ user: null, token: null });
      return { success: true };
    }
  },

  // Refresh token
  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh');
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      set({ token: access_token });
      return { success: true };
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      set({ user: null, token: null });
      return { success: false };
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
